import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { AppContext } from "Context/AppContext";
import { ArsContext } from "./Contexts/ArsContext";
import { toast } from "react-toastify";

import ArsLogsTable from "./Components/ArsLogsTable";
import NoDataComponent from "Components/Common/NoDataComponent";
import TableSkeletonElement from "Components/GMS/Products/components/TableSkeletonElement";
import ConfirmModal from "./Components/ConfirmModal";
import AppletModal from './Components/AppletModal';
import Paginator from "Components/Common/PaginatorWithApi";
import PromoteRecommenderModel from "./Components/PromoteRecommenderModel";

import { createRecommenderModel, generateRecommendations, generateRecommender, getRecommenderLogs } from "./ARSServices/ARSServices";
import { checkNotGeneratedStatus } from "./ARSHelper";

import { ARS_GENERATE_MESSAGE, ARS_GENERATION_SUCCESS, GENERATE, PROCEED, PROMOTE, COMMEND, CREATE, TITLE, RECOMMENDER_READ, RECOMMENDER_CREATE, RECOMMENDER_PROMOTE, RECOMMENDER_GENERATE, RECOMMENDER_UPDATE, RECOMMENDER_DELETE } from "./ARSConstants";
import GenerateStatus from "./Components/GenerateStatus";
import ReloadButton from "./Components/ReloadButton";
import HIstoryTable from "./Components/HIstoryTable";
import { TCreateModel } from "../ABTesting/types/ab";
import CreateModelDialog from "./Components/CreateModelDialog";
import { RecommenderLogsResponse } from "./Types";

const ARSComponent = () => {
  const appData = useContext(AppContext);
  const { logsData, setLogsData, checkGenerateStatus, showStatus, showPromote } = useContext(ArsContext);
  const history = useHistory();

  const [loading, setLoading] = useState<boolean>(false);
  const [viewLog, setViewLog] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState<boolean>(false);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [promoteModal, setPromoteModal] = useState<boolean>(false);

  const [showApplet, setShowApplet] = useState<boolean>(false);
  const [isInitialArsLoad, setIsInitialArsLoad] = useState<boolean>(true);

  const [promoteRecommenderModel, setPromoteRecommenderModal] = useState<boolean>(false);
  const [logId, setLogId] = useState<string>('');
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedItems, setSelectedItems] = useState<RecommenderLogsResponse[]>([]);
  const [paginationParams, setPaginationParams] = useState<TPaginationParams>({ per_page: 10, page: 1, sort_by: "updatedOn", sort_by_order: "desc" });

  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [createModelLoader, setCreateModelLoader] = useState<boolean>(false);
  const openDialog = () => setShowCreateDialog(true);
  const closeDialog = () => setShowCreateDialog(false);

  const createPermission = appData.pm.checkPermission(RECOMMENDER_CREATE);
  const readPermission = appData.pm.checkPermission(RECOMMENDER_READ);
  const updatePermission = appData.pm.checkPermission(RECOMMENDER_UPDATE);
  const deletePermission = appData.pm.checkPermission(RECOMMENDER_DELETE);
  const promotePermission = appData.pm.checkPermission(RECOMMENDER_PROMOTE);
  const generatePermission = appData.pm.checkPermission(RECOMMENDER_GENERATE);
  const industryId = appData?.get_selected_industry()?.id;
  const channelKey = appData?.channelKey;

  useEffect(() => {
    if (!readPermission) {
      history.push("/workspace/module/permission/denied");
      return;
    }
    if (appData?.channelKey) {
      getRecLogs();
      const intervalId = setInterval(getRecLogs, 2 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [appData.channelKey, paginationParams]);

  useEffect(() => {
    if (!isInitialArsLoad && !showStatus) {
      getRecLogs();
    }
  }, [showStatus])

  const onFieldSubmit = async (modelName: string, type: "Co-seller" | "Substitution", defaultSubmodel: boolean) => {
    if (!industryId || !channelKey) return
    if (!createPermission) {
      toast.warn('You do not have permission to create model');
      return;
    }
    const model: TCreateModel = { industry_id: industryId, channel_id: channelKey, model_name: modelName, type: type, require_default_submodel: defaultSubmodel };
    try {
      setCreateModelLoader(true);
      const res = await createRecommenderModel(model);
      if (res?.done) {
        getRecLogs();
        if (res?.model_id) history.push(`ars/model/${res?.model_id}`);
      }
      else {
        toast.info(res?.message);
      }
    } catch (error) {
      console.log(error);
      setCreateModelLoader(false);
    } finally {
      setCreateModelLoader(false);
      closeDialog();
    }
  }

  const getRecLogs = useCallback(async () => {
    if (!appData?.channelKey) return;

    if (isInitialArsLoad) {
      setLoading(true);
    }

    try {
      const res = await getRecommenderLogs(appData.channelKey, paginationParams);
      if (res?.done) {
        console.log(res);

        const logs = res?.data ?? [];
        setLogsData(logs);
        setTotalRecords(res?.total ?? 0);
      } else {
        toast.error(res?.message);
      }

    } catch (error) {
      console.error('Error fetching recommender logs:', error);
      toast.error("Failed to fetch recommender logs. Please try again.");
    } finally {
      setLoading(false);
      if (isInitialArsLoad) {
        setIsInitialArsLoad(false);
      }
    }
  }, [appData?.channelKey, paginationParams, setLogsData]);

  const callGenerateAPI = useCallback(async () => {
    try {
      const response = await generateRecommendations('ARS', appData?.userName ?? '', appData?.channelKey ?? '');
      if (response?.done) {
        toast.success(ARS_GENERATION_SUCCESS);
      } else {
        throw new Error('Generate API call failed');
      }
    } catch (error) {
      console.error("Generate API call error:", error);
      toast.error("Failed to generate recommendations. Please try again.");
    }
  }, [appData?.userName, appData?.channelKey]);

  const generateARS = useCallback(async (modelIds: string[]) => {
    try {
      setLoading(true);
      const response = await generateRecommender(modelIds, appData?.channelKey ?? '');
      if (response?.done) {
        checkGenerateStatus(response?.job_id ?? '');
      } else {
        toast.success(response?.message ?? 'Something went wrong');
      }
    } catch (error) {
      console.error("ARS generation error:", error);
      toast.error("Failed to generate ARS. Please try again.");
    } finally {
      setLoading(false);
      setPromoteModal(false);
      setSelectedItems([]);
    }
  }, [appData?.channelKey, getRecLogs]);

  const handleOnConfirm = useCallback(() => {
    callGenerateAPI();
    closeConfirmModal();
  }, [callGenerateAPI]);

  const closeConfirmModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const closePromoteRecommenderModal = useCallback(() => {
    setPromoteRecommenderModal(false);
    setSelectedItems([]);
  }, []);

  const openPromoteRecommenderModal = useCallback(() => {
    if (!checkNotGeneratedStatus(selectedItems)) {
      setPromoteRecommenderModal(true);
    } else {
      toast.warning("You've selected a Not Generated model, please unselect it.");
    }
  }, [selectedItems]);

  const closeApplet = useCallback(() => {
    setShowApplet(false);
  }, []);

  const updatePageSize = useCallback((pageSize: number) => {
    setPaginationParams((prevState: TPaginationParams) => ({
      ...prevState,
      page: 1,
      per_page: pageSize,
    }));
  }, []);

  const handlePageChange = useCallback((pageNumber: number) => {
    setPaginationParams((prevState: TPaginationParams) => ({
      ...prevState,
      page: pageNumber,
    }));
  }, []);


  const isPromoteButtonDisabled = useMemo(() =>
    selectedItems.length === 0 ||
    selectedItems.length > 3 ||
    !promotePermission,
    [selectedItems, promotePermission]
  );

  const isGenerateButtonDisabled = useMemo(() =>
    selectedItems.length === 0 ||
    selectedItems?.length > 3 ||
    !generatePermission,
    [selectedItems, generatePermission]
  );


  return (
    <>
      <div className={`tw-flex tw-justify-between tw-border-b tw-border-[#D1D1D6] tw-pb-3`}>
        <div className={`tw-flex tw-flex-col tw-items-left tw-text-xs`}>
          <label className={`tw-text-[#091E42] tw-text-[24px] tw-font-semibold`}>{COMMEND}{showHistory && " - History"}</label>
        </div>
        <div className={`tw-flex tw-items-center tw-gap-4`}>
          <GenerateStatus isGenerate={showStatus} isPromote={showPromote} />
          {!loading && logsData?.length > 0 ? <button
            className="outline-button tw-border-none"
            type="button"
            onClick={() => setShowHistory(prev => !prev)}>
            {showHistory ? 'Commend' : 'History'}
          </button> : null}
          {!showHistory ? <ReloadButton loading={loading} onClick={getRecLogs} /> : null}
          <button
            className="outline-button"
            type="button"
            onClick={() => setShowApplet(prev => !prev)}
          >
            Applet
          </button>
          <button
            disabled={isPromoteButtonDisabled}
            className={`button-style tw-w-max`}
            onClick={() => {
              if (!promotePermission) {
                toast.warn('You do not have permission to promote model');
                return;
              }
              openPromoteRecommenderModal();
            }}
          >
            {PROMOTE}
          </button>
          <button
            disabled={isGenerateButtonDisabled}
            className={`button-style tw-w-max`}
            onClick={() => {
              if (!generatePermission) {
                toast.warn('You do not have permission to generate model');
                return;
              }
              setPromoteModal(true)
            }}
          >
            {GENERATE}
          </button>
          <button
            id="create-model-btn"
            className={`button-style tw-w-max`}
            disabled={!createPermission}
            onClick={() => {
              if (!createPermission) {
                toast.warn('You do not have permission to create model');
                return;
              }
              openDialog();
            }}>
            {"Create"}
          </button>
        </div>
      </div>

      {!showHistory && loading && <TableSkeletonElement />}

      {!loading && logsData?.length > 0 && !showHistory ? (
        <ArsLogsTable
          data={logsData}
          setViewLog={setViewLog}
          setLogId={setLogId}
          getRecLogs={getRecLogs}
          setLoading={setLoading}
          promoteModal={promoteModal}
          setPromoteModal={setPromoteModal}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          generateRecommender={generateARS}
          paginationParams={paginationParams}
          setPaginationParams={setPaginationParams}
          createPermission={createPermission}
          updatePermission={updatePermission}
          deletePermission={deletePermission}
        />
      ) : null}

      {showHistory ? <HIstoryTable merchantId={appData?.channelKey ?? ''} /> : null}

      {!loading && logsData?.length === 0 && (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full">
          <NoDataComponent title="No models created" />
          <button
            id="create-model-button"
            className="button-style tw-w-max tw-mt-4"
            disabled={!createPermission}
            onClick={() => {
              if (!createPermission) {
                toast.warn('You do not have permission to create model');
                return;
              }
              openDialog();
            }}
          >
            Create Model
          </button>
        </div>
      )}

      {!loading && logsData?.length > 0 && !showHistory ? (
        <div className="tw-py-2">
          <div className={`tw-flex tw-justify-end tw-items-center tw-space-x-3 tw-flex-wrap`}>
            <div className={`tw-flex tw-items-center tw-space-x-3`}>
              <span>Show</span>
              <select
                className={`tw-border focus:tw-outline-none tw-p-1`}
                style={{ width: "80px" }}
                aria-label="Records on page select"
                value={paginationParams.per_page}
                onChange={(e) => updatePageSize(parseInt(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
            <Paginator
              onPageChange={handlePageChange}
              recordPerPage={paginationParams.per_page}
              presentPage={paginationParams.page}
              totalRecords={totalRecords}
            />
          </div>
        </div>
      ) : null}

      <ConfirmModal
        showModal={showModal}
        closeModal={closeConfirmModal}
        onConfirm={handleOnConfirm}
        primaryMessage={ARS_GENERATE_MESSAGE}
        secondaryMessage={PROCEED}
      />

      {showApplet && (
        <AppletModal
          showApplet={showApplet}
          closeApplet={closeApplet}
          channelId={appData?.channelKey ?? ''}
        />
      )}

      <PromoteRecommenderModel
        showModal={promoteRecommenderModel}
        closePromoteModal={closePromoteRecommenderModal}
        selectedItem={selectedItems}
        channelKey={appData?.channelKey ?? ''}
        setSelectedItems={setSelectedItems}
        getRecLogs={getRecLogs}
      />

      {showCreateDialog ?
        <CreateModelDialog
          id={`create-model`}
          dialogHeader={CREATE}
          labelName={TITLE}
          onClose={closeDialog}
          onFieldSubmit={onFieldSubmit}
          showIndicator={createModelLoader}
        /> : null}

      {/* <ConfirmModal 
        showModal={showConfirmModal} 
        closeModal={closePromoteConfirmModel} 
        onConfirm={promoteModel} 
        primaryMessage={'You have selected new model(s) to replace the live model in the associated Applet(s)'} 
        secondaryMessage={PROCEED}  
      /> */}
    </>
  );
};

export default ARSComponent;